import Gettext, { type GettextInterface, type LocaleData } from "@indeed/indeed-gettext";
import { createContext, useContext, useMemo } from "react";

import { TextDirectionProvider } from "../../mads/util/TextDirection";
import { buildI18n } from "./";
import { emptyCatalog } from "./Catalog";
import type { Locale } from "./Locale";
import { LocaleProvider } from "./LocaleContext";

/**
 * Determine the text direction based on the locale
 *
 * Arabic and Hebrew are RTL languages, everything else is LTR.
 */
export function textDirectionFromLocale(locale: Locale): "ltr" | "rtl" {
  const language = locale.language;
  return language === "ar" || language === "he" ? "rtl" : "ltr";
}

export const GettextContext = createContext<GettextInterface>(Gettext(emptyCatalog()));

export function useI18n(): GettextInterface {
  return useContext(GettextContext);
}

/**
 * Provides common I18n-related context to the app
 *
 * Specifically the gettext instance, the locale and the text direction.
 */
export function I18nProvider({
  children,
  locale,
  catalog,
}: {
  children: React.ReactNode;
  locale: Locale;
  /**
   * Preloaded gettext catalog for the active locale. When provided, the gettext
   * instance is built from it directly instead of the bundled all-locales
   * catalog — this lets web SSR ship only the active locale to the client
   * (RN-6851 / DISCO-5470). When absent, falls back to the bundled loadCatalog,
   * so existing callers are unaffected.
   */
  catalog?: LocaleData;
}) {
  const i18n = useMemo(() => (catalog ? Gettext(catalog) : buildI18n(locale)), [catalog, locale]);
  const textDirection = textDirectionFromLocale(locale);
  return (
    <GettextContext.Provider value={i18n}>
      <LocaleProvider locale={locale}>
        <TextDirectionProvider textDirection={textDirection}>{children}</TextDirectionProvider>
      </LocaleProvider>
    </GettextContext.Provider>
  );
}
